import { Input, ModelInference } from 'interfaces/calculateFeatures';
import { ClassificationModel } from 'interfaces/classificationModel';
import { find, isEmpty } from 'lodash';
import { getModelId, ModelUserPreferences } from 'utils/models';

export interface ResolvedModelPreference {
  preferenceKey: string;
  preferenceValue: ModelUserPreferences;
}

// This module is intentionally binning-focused.
// Keep it narrow for now so rebuild/preset logic for binning is easy to reason about and test.
// If we later decide to normalize other model preferences here (e.g., classesToUse/classesToIgnore),
// add dedicated helpers and tests instead of widening existing function behavior implicitly.

export const getBinningPreference = (input?: Input): Pick<ModelUserPreferences, 'numBins' | 'mapValuesToBins'> => {
  // Classification models never use the per-probe list-shaped binning, only spot detection does.
  const binning = Array.isArray(input?.binning) ? undefined : input?.binning;
  return {
    numBins: binning?.numBins,
    mapValuesToBins: binning?.mapValuesToBins,
  };
};

export const hasBinningPreference = (input?: Input): boolean => {
  const binning = Array.isArray(input?.binning) ? undefined : input?.binning;
  return Boolean(binning?.numBins) || !isEmpty(binning?.mapValuesToBins);
};

// For inference-model inputs, preferences are keyed by modelId.
// We retain readAs together with binning because UI selection and payload serialization use both.
export const resolveInferenceInputModelPreference = (params: {
  resultInput: Input;
  currentModel?: ModelInference;
  classificationModelOptions: ClassificationModel[];
}): ResolvedModelPreference | null => {
  const { resultInput, currentModel, classificationModelOptions } = params;
  const preferredModelType = resultInput?.readAs ?? currentModel?.modelType;

  const isClassificationModel = preferredModelType
    ? Boolean(find(classificationModelOptions, { id: preferredModelType }))
    : false;
  const hasAreaTransferPreference = resultInput?.transferAreasToRegisteredStains !== undefined;

  if (!isClassificationModel && !hasBinningPreference(resultInput) && !hasAreaTransferPreference) {
    return null;
  }

  const modelId = currentModel?.modelId ?? getModelId(currentModel?.modelUrl);
  if (!modelId) {
    return null;
  }

  return {
    preferenceKey: modelId,
    preferenceValue: {
      readAs: resultInput?.readAs,
      ...getBinningPreference(resultInput),
      ...(hasAreaTransferPreference
        ? { transferAreasToRegisteredStains: resultInput.transferAreasToRegisteredStains }
        : {}),
    },
  };
};

// For non-model result inputs (e.g., quantification artifacts), preferences are keyed by resultKey.
export const resolveGenericResultBinningPreference = (params: {
  resultKey: string;
  resultInput: Input;
}): ResolvedModelPreference | null => {
  const { resultKey, resultInput } = params;

  if (!hasBinningPreference(resultInput)) {
    return null;
  }

  return {
    preferenceKey: resultKey,
    preferenceValue: {
      ...getBinningPreference(resultInput),
    },
  };
};
